import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Building2, MapPin, Clock, Award, FileText, Shield, TrendingUp, HelpCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import RelatedCaseStudies from '@/components/RelatedCaseStudies';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { pages as pageContent } from '@/data/pageContent';

const CommercialEPC = () => {
  // pageContent is the single source of truth for FAQs (and their FAQPage
  // schema) on this route — the SSG build script consumes the same module,
  // so build-time HTML and React runtime render an identical FAQ list.
  // Note: the hero H1 and intro copy on this page are author-tailored
  // marketing copy separate from the module's semantic H1/intro used in the
  // SSG stub; that divergence is intentional.
  const routeContent = pageContent['/commercial-epc'];
  const faqs = routeContent.faqs.map((f) => ({ question: f.q, answer: f.a }));

  const serviceTypes = [
    {
      icon: FileText,
      title: "Level 3 Commercial EPC (NDEPC)",
      desc: "Offices, shops, warehouses, industrial units, pubs, cafés, surgeries and most SME commercial premises across Devon, Cornwall, Somerset and Dorset."
    },
    {
      icon: Shield,
      title: "MEES Commercial Advice Report",
      desc: "Practical, engineering-led consultancy for F/G rated buildings — modelled improvement options, costs, payback and the path to Band E, C and eventually B."
    },
    {
      icon: TrendingUp,
      title: "SBEM Calculations",
      desc: "New-build and major refurbishment SBEM modelling for Part L Building Regulations compliance and as-built NDEPCs."
    },
    {
      icon: MapPin,
      title: "Countywide Coverage",
      desc: "Devon, Cornwall, Somerset and Dorset. NDEA qualified assessors based in Exeter, covering Plymouth, Torbay, Taunton, Truro, Bournemouth and beyond."
    }
  ];

  const locations = [
    { county: "Devon", link: "/commercial-epc-devon", towns: "Exeter, Plymouth, Torbay, Newton Abbot, Barnstaple, Exmouth" },
    { county: "Cornwall", link: "/commercial-epc-cornwall", towns: "Truro, Falmouth, Newquay, Penzance, St Austell, Bodmin" },
    { county: "Somerset", link: "/commercial-epc-somerset", towns: "Taunton, Bridgwater, Yeovil, Chard, Glastonbury, Wells" },
    { county: "Dorset", link: "/commercial-epc-dorset", towns: "Bournemouth, Poole, Dorchester, Weymouth, Bridport" }
  ];

  return (
    <>
      <Helmet>
        <title>Commercial EPC (NDEPC) | Workshop, Warehouse, Factory, Office & Shop EPCs from £180 | Energy Performance Direct</title>
        <meta name="description" content="Accredited NDEA Commercial EPC (NDEPC) assessments from £180 — workshops, warehouses, factories, industrial units, offices, shops and pubs. SBEM modelling, MEES advice and Commercial Energy Advice Reports across Devon, Cornwall, Somerset, Dorset and beyond." />
        <meta name="keywords" content="commercial EPC, non-domestic EPC, NDEPC, commercial energy assessor, non-domestic energy assessor, warehouse EPC, workshop EPC, factory EPC, industrial EPC, office EPC, shop EPC, pub EPC, commercial EPC Exeter, commercial EPC Devon, SBEM calculation, MEES commercial landlord, commercial energy advice, NDEAR" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/commercial-epc" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Non-Domestic Energy Performance Certificate (NDEPC)",
            "name": "Commercial EPC Assessments South West",
            "description": "Accredited Level 3 Commercial EPC (NDEPC) assessments across Devon, Cornwall, Somerset and Dorset by qualified Non-Domestic Energy Assessors. SBEM calculations and MEES advice also available.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Energy Performance Direct",
              "telephone": "+44-1392-980077",
              "email": "info@energyperformancedirect.co.uk",
              "priceRange": "£180 - £1500",
              "areaServed": ["Devon", "Cornwall", "Somerset", "Dorset"]
            },
            "offers": {
              "@type": "Offer",
              "priceCurrency": "GBP",
              "price": "180",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "priceCurrency": "GBP",
                "minPrice": "180"
              }
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(f => ({
              "@type": "Question",
              "name": f.question,
              "acceptedAnswer": { "@type": "Answer", "text": f.answer }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">NDEA Accredited · Devon · Cornwall · Somerset · Dorset</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                Commercial EPCs — Workshops, Warehouses, Factories, Offices & Shops
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                Non-Domestic Energy Performance Certificates (NDEPCs) from £180, produced by an experienced NDEA using SBEM modelling. Workshops, warehouses, factories, industrial units, offices, shops and pubs are a core part of our work. Where a building needs to be improved rather than simply certified, we pair the EPC with a Commercial Energy Advice Report and a clear MEES pathway.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/booking" data-testid="commercial-epc-book-btn">
                  <Button className="bg-gradient-to-r from-green-600 to-cyan-500 hover:from-green-700 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-full shadow-lg">
                    Get a Quote in 2 Minutes
                  </Button>
                </Link>
                <Link to="/quick-quote" data-testid="commercial-epc-quick-quote-btn">
                  <Button variant="outline" className="border-2 border-cyan-600 text-cyan-700 hover:bg-cyan-50 px-8 py-6 text-lg rounded-full">
                    Request a Custom Quote
                  </Button>
                </Link>
                <a href="tel:01392980077" data-testid="commercial-epc-call-btn">
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Row */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className="text-center">
                <Award className="w-10 h-10 text-green-600 mx-auto mb-2" />
                <h3 className="text-base font-bold">NDEA Qualified</h3>
                <p className="text-sm text-gray-600">Elmhurst & Quidos accredited</p>
              </div>
              <div className="text-center">
                <Clock className="w-10 h-10 text-cyan-500 mx-auto mb-2" />
                <h3 className="text-base font-bold">5–7 Day Turnaround</h3>
                <p className="text-sm text-gray-600">From survey to lodged NDEPC</p>
              </div>
              <div className="text-center">
                <MapPin className="w-10 h-10 text-green-600 mx-auto mb-2" />
                <h3 className="text-base font-bold">4-County Coverage</h3>
                <p className="text-sm text-gray-600">Devon, Cornwall, Somerset, Dorset</p>
              </div>
              <div className="text-center">
                <Building2 className="w-10 h-10 text-cyan-500 mx-auto mb-2" />
                <h3 className="text-base font-bold">From £180</h3>
                <p className="text-sm text-gray-600">Transparent Level 3 pricing</p>
              </div>
            </div>
          </div>
        </section>

        {/* What We Cover */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-base md:text-lg font-semibold text-green-600 uppercase tracking-wider mb-2 text-center">Non-Domestic Energy Services</h2>
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">Everything a commercial landlord or owner needs</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {serviceTypes.map((s, idx) => {
                  const Icon = s.icon;
                  return (
                    <Card key={idx} className="border-none shadow-lg hover:shadow-xl transition-all">
                      <CardContent className="p-6">
                        <Icon className="w-10 h-10 text-green-600 mb-4" />
                        <h4 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h4>
                        <p className="text-sm text-gray-700">{s.desc}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Body content */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto prose prose-lg text-gray-700 space-y-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Level 3 Commercial EPCs — our specialism</h2>
              <p>
                Most commercial buildings in the South West — small offices, corner shops, retail parades, trade counters, warehouses, workshops, light industrial units, village pubs, B&amp;Bs, cafés, surgeries, nurseries and community halls — sit within the <strong>Level 3 Non-Domestic EPC</strong> category. That is exactly what we assess every day.
              </p>
              <p>
                A Level 3 assessment keeps the survey time proportionate, avoids unnecessary modelling complexity and keeps the fee competitive — while still producing a fully lodged, legally compliant NDEPC that meets the Energy Performance of Buildings (England and Wales) Regulations 2012 and can be used for sale, letting, MEES evidence, planning and lender due diligence.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">What&rsquo;s included in our survey</h2>
              <ul className="space-y-2 list-none pl-0">
                <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> <span>On-site measured survey of floor areas, zones, fabric construction and services</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> <span>U-value assessment of walls, floors, roof, glazing and doors</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> <span>Heating, hot water, cooling, ventilation and lighting inventory</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> <span>SBEM calculation and production of the official NDEPC asset rating</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> <span>Lodgement on the central non-domestic register and a plain-English summary</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> <span>Recommendations report listing realistic improvement measures</span></li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">MEES compliance &mdash; built into every quote</h2>
              <p>
                Since April 2023 no commercial property in England or Wales can be let with an EPC below Band E. In DESNZ&rsquo;s <a href="https://www.gov.uk/government/consultations/non-domestic-private-rented-sector-minimum-energy-efficiency-standards-epc-b-implementation/outcome/minimum-energy-efficiency-standards-mees-in-the-non-domestic-private-rented-sector-interim-response" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">June 2026 interim response</a>, the previously proposed 2027 EPC C milestone was dropped and a proposed <strong>EPC B by 2031</strong> now applies only to buildings <strong>over 1,000 m<sup>2</sup></strong>, where cost-effective and once secondary legislation passes. Smaller buildings remain at Band E with no further deadline currently defined. That&rsquo;s why our EPC work and our <Link to="/services/commercial_advice" className="text-green-600 hover:text-green-700 underline">Commercial Energy Advice consultancy</Link> are joined up.
              </p>
              <p>
                If your building is rated D, E, F or G we&rsquo;ll flag the practical upgrades — LED re-lamping, heating controls, destratification, roof insulation, cavity fill, solar PV, heat pump retrofit &mdash; that will actually move the rating, with indicative cost and payback. We&rsquo;re independent of any installer, so the advice is engineering-led not sales-led. Read more about <Link to="/mees-commercial-buildings" className="text-green-600 hover:text-green-700 underline">MEES for commercial buildings</Link> and our <Link to="/case-study-london-mees-unit-split" className="text-green-600 hover:text-green-700 underline">London G-to-B case study</Link>.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">SBEM &amp; new-build commercial EPCs</h2>
              <p>
                Alongside the core NDEPC service we provide full <strong><Link to="/sbem-explained" className="text-green-600 hover:text-green-700 underline">SBEM calculations</Link></strong> for new-build and major refurbishment projects requiring Part L Building Regulations compliance, plus the as-built commercial EPC required for sign-off. This is the same SBEM engine that produces the EPC asset rating &mdash; getting the modelling right at design stage often makes the difference between passing Part L cleanly and a costly rework.
              </p>
            </div>
          </div>
        </section>

        {/* Counties */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 text-center">Choose your county</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {locations.map((loc, idx) => (
                  <Link key={idx} to={loc.link} className="block p-6 bg-white border border-gray-200 rounded-xl hover:shadow-lg hover:border-green-400 transition-all">
                    <MapPin className="w-8 h-8 text-green-600 mb-3" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Commercial EPC {loc.county}</h3>
                    <p className="text-sm text-gray-600">{loc.towns}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 text-center">Commercial EPC FAQs</h2>
              <div className="space-y-6">
                {faqs.map((faq, idx) => (
                  <Card key={idx} className="border-none shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start gap-2">
                        <HelpCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                        {faq.question}
                      </h3>
                      <p className="text-gray-700">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related case studies — commercial only */}
        <RelatedCaseStudies
          title="See it in practice"
          subtitle="Real commercial buildings we've assessed and modelled, with the rating journey, costs and final outcome."
          filterType="commercial"
          limit={3}
        />

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-green-600 to-cyan-500">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Book your Commercial EPC today</h2>
              <p className="text-lg mb-8 opacity-95">
                Level 3 NDEPCs and SBEM calculations across Devon, Cornwall, Somerset and Dorset. Accredited, engineering-led and MEES-ready.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/booking" data-testid="commercial-epc-cta-book">
                  <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                    Get a Quote in 2 Minutes
                  </Button>
                </Link>
                <a href="tel:01392980077" data-testid="commercial-epc-cta-call">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Call 01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CommercialEPC;
